// apps/web/src/components/onboarding/WheelDatePicker.tsx
// iOS-style date wheel — three scroll-snap columns (month · day · year), no
// popup. The centered row of each column is the selection; a lime band marks
// it. Emits YYYY-MM-DD via onChange. Day count follows the chosen month/year
// (Feb 28/29), and the selected day is clamped when the month changes.
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../../lib/i18n";
import { hapticTick } from "../../lib/haptics";
import { clampDay, daysInMonth, monthNames, parseISO, toISO } from "./dateWheel";

const ITEM_H = 48;   // px per row — tall enough that the centered pick reads big
const PAD = ITEM_H * 2; // two blank rows top & bottom so ends can center

interface Props {
  /** YYYY-MM-DD, or "" to start at the fallback (fallbackYear-06-15). */
  value: string;
  onChange: (iso: string) => void;
  minYear: number;
  maxYear: number;
  /** Year the empty state centers on (e.g. 25 years ago). */
  fallbackYear: number;
}

export function WheelDatePicker({ value, onChange, minYear, maxYear, fallbackYear }: Props) {
  const { locale } = useLocale();
  const months = useMemo(() => monthNames(locale), [locale]);
  const years = useMemo(
    () => Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i),
    [minYear, maxYear],
  );

  const parts = parseISO(value, fallbackYear);
  const [year, setYear] = useState(parts.year);
  const [month1, setMonth1] = useState(parts.month1);
  const [day, setDay] = useState(parts.day);

  // External value change (rare — e.g. reset) re-seeds the wheels.
  const lastEmitted = useRef(value);
  useEffect(() => {
    if (value === lastEmitted.current) return;
    const p = parseISO(value, fallbackYear);
    setYear(p.year); setMonth1(p.month1); setDay(p.day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Emit whenever a committed part changes (day clamped to the month).
  useEffect(() => {
    const d = clampDay(year, month1, day);
    if (d !== day) { setDay(d); return; }
    const iso = toISO(year, month1, d);
    if (iso !== lastEmitted.current) { lastEmitted.current = iso; onChange(iso); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month1, day]);

  const dayCount = daysInMonth(year, month1);
  const dayItems = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => String(i + 1)),
    [dayCount],
  );

  return (
    <div className="relative select-none" style={{ height: ITEM_H * 5 }}>
      {/* centered selection band */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 rounded-xl bg-surface-strong/60"
        style={{ height: ITEM_H }}
      />
      {/* top & bottom fade so off-center rows recede. --bg-base is a full
          color value (not a triplet), and it follows the active theme. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10"
        style={{ background: "linear-gradient(180deg, var(--bg-base) 0%, transparent 35%, transparent 65%, var(--bg-base) 100%)" }} />
      <div className="relative flex h-full">
        <Wheel items={months} index={month1 - 1} onIndex={(i) => setMonth1(i + 1)} align="end" />
        <Wheel items={dayItems} index={Math.min(day, dayCount) - 1} onIndex={(i) => setDay(i + 1)} align="center" narrow />
        <Wheel items={years.map(String)} index={year - minYear} onIndex={(i) => setYear(minYear + i)} align="start" />
      </div>
    </div>
  );
}

function Wheel({ items, index, onIndex, align, narrow }: {
  items: string[]; index: number; onIndex: (i: number) => void;
  align: "start" | "center" | "end"; narrow?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const settle = useRef<ReturnType<typeof setTimeout>>();
  const programmatic = useRef(false);
  // The row visually highlighted RIGHT NOW. Tracks the finger during a fling
  // (state, not just a ref) so the big centered value updates live instead of
  // only after the scroll settles — the "responsive" the owner asked for.
  const [live, setLive] = useState(index);

  // Keep the DOM scroll position in sync when the selected index changes from
  // outside (initial mount, day-clamp, external reset).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setLive(index);
    const target = index * ITEM_H;
    if (Math.abs(el.scrollTop - target) > 1) {
      programmatic.current = true;
      el.scrollTo({ top: target, behavior: "auto" });
      // release the guard after the scroll settles
      setTimeout(() => { programmatic.current = false; }, 60);
    }
  }, [index]);

  const liveIdx = useRef(index);
  const onScroll = () => {
    if (programmatic.current) return;
    const el = ref.current;
    if (el) {
      // Haptic tick + live highlight each time a new row crosses the band.
      const now = Math.min(Math.max(0, Math.round(el.scrollTop / ITEM_H)), items.length - 1);
      if (now !== liveIdx.current) { liveIdx.current = now; setLive(now); hapticTick(); }
    }
    clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      if (!el) return;
      const i = Math.round(el.scrollTop / ITEM_H);
      const clamped = Math.min(Math.max(0, i), items.length - 1);
      // snap exactly onto the row, then report
      programmatic.current = true;
      el.scrollTo({ top: clamped * ITEM_H, behavior: "smooth" });
      setTimeout(() => { programmatic.current = false; }, 180);
      if (clamped !== index) onIndex(clamped);
    }, 80);
  };

  // No CSS scroll-snap here: on iOS `snap-mandatory` clamps the fling and the
  // wheel feels sticky. Free momentum + the settle-snap above = UIPickerView.
  const justify = align === "start" ? "justify-start pl-3" : align === "end" ? "justify-end pr-3" : "justify-center";
  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className={`scrollbar-hidden h-full overflow-y-auto overscroll-contain [touch-action:pan-y] ${narrow ? "w-16" : "flex-1"}`}
    >
      <div style={{ height: PAD }} aria-hidden="true" />
      {items.map((label, i) => (
        <div
          key={label + i}
          onClick={() => onIndex(i)}
          className={`flex items-center ${justify} tabular-nums transition-[color,opacity,transform] duration-100 ${
            i === live ? "text-fg font-bold text-[1.45rem]" : "text-fg-muted opacity-60 text-[1.05rem]"
          }`}
          style={{ height: ITEM_H }}
        >
          {label}
        </div>
      ))}
      <div style={{ height: PAD }} aria-hidden="true" />
    </div>
  );
}
